import { palette, Popover } from "lib/miolo";
import BarChart from "components/company/sections/summary/adornments/BarChart";
import numeral from "numeral";
import type { ReactNode } from "react";
import { useState } from "react";
import type { IDividendsPaidDetails, IDividendsPaidByYearDetails, IDividendYieldDetails } from "types/graphqlTypes";

function formatDividendYield(dividendYield: number) {
  return numeral(dividendYield * 100)
    .format("0.0")
    .replace(".0", "")
    .replace(".", ",");
}

interface IDividendYieldSnapshotPopoverProps {
  dividendYieldSnapshotEachSymbol: IDividendYieldDetails[];
  children: ReactNode;
}

function DividendYieldSnapshotPopover({
  dividendYieldSnapshotEachSymbol,
  children,
}: IDividendYieldSnapshotPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover
      id="DividendYieldSnapshotPopover"
      isOpen={isOpen}
      arrowColor={palette.white}
      backgroundColor={palette.white}
      onClose={() => setIsOpen(false)}
      render={() => {
        return (
          <div className="grid max-w-xs grid-cols-1 gap-x-3 gap-y-2 p-4 font-aeonik text-sm text-gray-700">
            {[...dividendYieldSnapshotEachSymbol]
              .sort((a, b) => {
                return b.dividendYield - a.dividendYield;
              })
              .map((node, index) => {
                const amountPaid = numeral(node.amountPaid).format("0.00").replace(".", ",");

                return (
                  <div key={node.tickerSymbol} className="p-1">
                    <strong className="text-xl font-bold tracking-widest">
                      {formatDividendYield(node.dividendYield)}%
                    </strong>

                    <div className="text-sm font-medium tracking-wider text-gray-500">
                      {node.tickerSymbol} • R${amountPaid} {index === 0 ? "(Total Pago)" : null}
                    </div>
                  </div>
                );
              })}
          </div>
        );
      }}
    >
      {({ accessibility, ref }) => (
        <button
          {...accessibility}
          ref={ref}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="m-0 inline-flex cursor-pointer border-b border-gray-700 bg-transparent p-0 text-inherit hover:border-blue-700 hover:text-blue-700"
        >
          {children}
        </button>
      )}
    </Popover>
  );
}

interface IDividendsPaidSummaryProps {
  dividendsPaid: IDividendsPaidDetails[];
  dividendsPaidByYear: IDividendsPaidByYearDetails[];
  dividendYieldSnapshotEachSymbol: IDividendYieldDetails[];
}

export default function DividendsPaidSummary({
  dividendsPaid,
  dividendsPaidByYear,
  dividendYieldSnapshotEachSymbol,
}: IDividendsPaidSummaryProps) {
  const [lastDividendPaid] = dividendsPaid;
  if (dividendYieldSnapshotEachSymbol.length === 0) {
    return null;
  }

  const [topDividendYieldSnapshot] = [...dividendYieldSnapshotEachSymbol];

  if (lastDividendPaid === null || !topDividendYieldSnapshot) {
    return null;
  }

  const { dividendYield } = topDividendYieldSnapshot;

  return (
    <div className="flex flex-col items-center space-y-4 sm:flex-row">
      <div className="rounded-xl bg-slate-100 px-4 py-2">
        <div className="flex space-x-1 text-lg">
          <strong className="font-bold text-gray-900">{formatDividendYield(dividendYield)}%</strong>
          <span className="text-gray-900">{" • "}</span>
          <div>
            <DividendYieldSnapshotPopover dividendYieldSnapshotEachSymbol={dividendYieldSnapshotEachSymbol}>
              {topDividendYieldSnapshot.tickerSymbol}
            </DividendYieldSnapshotPopover>
          </div>
        </div>

        <span className="text-sm font-medium text-gray-600">DY dos últimos 12 meses</span>
      </div>

      {dividendsPaidByYear.length > 1 && (
        <div className="ml-6">
          <BarChart
            ariaLabel="Gráfico demonstrando a distribuição dos Dividendos ao longo dos últimos 20 anos"
            chart={{
              margin: { top: 0, left: 0, bottom: 0, right: 0 },
              padding: { top: 4, left: 4, bottom: 4, right: 4 },
              primaryColor: palette.copper,
              width: 280,
              height: 40,
            }}
            nodes={dividendsPaidByYear
              .map((node) => {
                return {
                  date: new Date(node.fiscalYear, 1, 1),
                  value: node.amountPaid,
                };
              })
              .slice(0, 10)}
          />
        </div>
      )}
    </div>
  );
}
